import { Component } from '@theme/component';

/**
 * @typedef {Object} StickyMobileCtaRefs
 * @property {HTMLElement} bar - The sticky bar
 */

/**
 * Mobile sticky rating and link bar for the overlay group.
 *
 * Visibility follows sticky-add-to-cart: stay hidden until a trigger has
 * scrolled above the viewport, then hide again when the footer reaches the
 * bar so the footer stays usable. Product pages keep sticky-add-to-cart.
 *
 * @extends {Component<StickyMobileCtaRefs>}
 */
class StickyMobileCta extends Component {
  requiredRefs = ['bar'];

  /** @type {IntersectionObserver | null} */
  #triggerObserver = null;

  /** @type {IntersectionObserver | null} */
  #endObserver = null;

  /** @type {boolean} */
  #pastTrigger = false;

  /** @type {boolean} */
  #atEnd = false;

  /** @type {Set<Element>} */
  #visibleEnds = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#disconnectObservers();

    if (document.documentElement.classList.contains('shopify-design-mode')) {
      this.#setStuck(true);
      return;
    }

    if (document.querySelector('sticky-add-to-cart')) {
      this.#setStuck(false);
      return;
    }

    const main = document.querySelector('#MainContent');
    const firstSection = main?.querySelector(':scope > .shopify-section');
    const footerSections = document.querySelectorAll('.shopify-section-group-footer-group, footer');

    if (!firstSection) {
      this.#pastTrigger = true;
    } else {
      this.#triggerObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        const aboveViewport = entry.boundingClientRect.bottom <= 0;
        this.#pastTrigger = !entry.isIntersecting && aboveViewport;
        this.#sync();
      });
      this.#triggerObserver.observe(firstSection);
    }

    if (footerSections.length > 0) {
      const barHeight = Math.max(this.refs.bar?.offsetHeight ?? 0, 72);
      // Grow the root downward by the bar height so the bar hides as the footer
      // approaches, before the bar can cover footer content.
      this.#endObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.#visibleEnds.add(entry.target);
            } else {
              this.#visibleEnds.delete(entry.target);
            }
          }

          this.#atEnd = this.#visibleEnds.size > 0;
          this.#sync();
        },
        { rootMargin: `0px 0px ${barHeight}px 0px` }
      );

      for (const footerSection of footerSections) {
        this.#endObserver.observe(footerSection);
      }
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectObservers();
  }

  #disconnectObservers() {
    this.#triggerObserver?.disconnect();
    this.#endObserver?.disconnect();
    this.#triggerObserver = null;
    this.#endObserver = null;
    this.#visibleEnds.clear();
  }

  #sync() {
    this.#setStuck(this.#pastTrigger && !this.#atEnd);
  }

  /**
   * @param {boolean} stuck
   */
  #setStuck(stuck) {
    const { bar } = this.refs;
    if (!bar) return;

    bar.dataset.stuck = stuck ? 'true' : 'false';

    if (stuck) {
      bar.removeAttribute('inert');
    } else {
      bar.setAttribute('inert', '');
    }
  }
}

if (!customElements.get('sticky-mobile-cta')) {
  customElements.define('sticky-mobile-cta', StickyMobileCta);
}
